import React from 'react';
import { Brain, Zap, BarChart3, Eye, Shield, TrendingUp, Clock, Target, Cpu } from 'lucide-react';
// @ts-ignore: module has no declaration file
import useDynamicSEO from '../hooks/dynamicSEO';

const AIParkingManagementPlatform: React.FC = () => {
    useDynamicSEO({
      title: "AI Parking Management Platform | Smart Intelligent App",
      description: "Predict & optimize parking operations using AI-powered decision making. Improve efficiency with intelligent forecasting, traffic insights & smart automation.",
          keywords:["Parking Management System","Event & Exhibition Parking Management","Parking Lot Management App","Digital Parking Solutions"," Vehicle entry exit management system","AI Parking Management Platform","Parking Lot Automation Software", "Parking revenue management app", "Tourist Attraction Parking Software", "Cinema & Multiplex Parking System"],
      ogTitle: "AI Parking Management Platform | Smart Intelligent App",
      ogDescription: "Predict & optimize parking operations using AI-powered decision making. Improve efficiency with intelligent forecasting, traffic insights & smart automation."
    });

  const handleWhatsAppDemo = () => {
    const phoneNumber = "919384376329";
    const message = "Hi! I'm interested in learning more about Park Hive's AI Parking Management Platform. Can you please provide me with a demo?";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const aiLearning = [
    "Seasonal, daily, and hourly demand trends",
    "Vehicle type distributions",
    "Entry-exit timestamps",
    "Staff performance patterns",
    "Occupancy surges",
    "Revenue flow and pricing impact"
  ];

  const aiEnsures = [
    "Congestion is prevented before it begins",
    "Entry-exit flow is optimized dynamically",
    "Pricing can adjust to demand intelligently",
    "Resources are deployed exactly where needed",
    "Revenue remains visible and accurate"
  ];

  const aiCapabilities = [
    "Predict peak-hour crowd surges",
    "Reduce queue build-up before it forms",
    "Optimize slot distribution across levels or zones",
    "Inform staffing needs and resource planning",
    "Identify abnormal entry-exit patterns or fraud attempts",
    "Deliver insights that help owners improve profitability"
  ];

  const intelligenceFeatures = [
    {
      icon: <BarChart3 className="h-6 w-6 text-blue-600" />,
      title: "Real-time analytics and predictive forecasting",
      description: "Advanced data intelligence for decision making"
    },
    {
      icon: <Zap className="h-6 w-6 text-green-600" />,
      title: "Automated traffic flow optimization based on live demand",
      description: "Dynamic flow control and congestion prevention"
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-purple-600" />,
      title: "Smart pricing strategy driven by usage behavior",
      description: "AI-powered revenue optimization"
    },
    {
      icon: <Target className="h-6 w-6 text-orange-600" />,
      title: "Intelligent occupancy management and forecasting",
      description: "Predictive capacity planning"
    },
    {
      icon: <Brain className="h-6 w-6 text-indigo-600" />,
      title: "AI-assisted entry and exit decisioning",
      description: "Intelligent access control automation"
    },
    {
      icon: <Shield className="h-6 w-6 text-red-600" />,
      title: "Fraud detection and abnormal pattern alerts",
      description: "Advanced security and anomaly detection"
    }
  ];

  const additionalFeatures = [
    "Comprehensive multi-site visibility from a single interface",
    "Cloud-based data intelligence accessible from anywhere"
  ];

  const scenarios = [
    { scenario: "Saturday night multiplex rush", icon: "🎬" },
    { scenario: "Airport early-morning peak", icon: "✈️" },
    { scenario: "Hospital emergency inflow", icon: "🏥" },
    { scenario: "Festival or exhibition overflow", icon: "🎪" },
    { scenario: "Tourist season surges", icon: "🗺️" },
    { scenario: "Corporate morning traffic waves", icon: "🏢" }
  ];

  const businessImpacts = [
    { before: "Guesswork operations", after: "Predictive planning", benefit: "Strategic advantage" },
    { before: "Manual workflows", after: "Automated processes", benefit: "Faster operations" },
    { before: "Hidden revenue", after: "Transparent tracking", benefit: "Trust building" },
    { before: "Fixed pricing", after: "Smart pricing", benefit: "Higher profitability" }
  ];

  const visionaryBenefits = [
    "Transform operational chaos into controlled intelligence",
    "Strengthen financial accuracy and eliminate leakage",
    "Deliver superior customer experience at every touchpoint",
    "Scale faster and manage multiple sites effortlessly",
    "Build a future-ready parking ecosystem"
  ];

  const faqs = [
    {
      question: "Is AI only for large facilities?",
      answer: "No — it adapts to any size, from small lots to multi-site enterprise operations."
    },
    {
      question: "Will existing staff be able to use it easily?",
      answer: "Yes — the platform simplifies operations rather than complicating them."
    },
    {
      question: "Does AI replace manual operators?",
      answer: "It enhances decision-making and eliminates dependency, not people."
    },
    {
      question: "Can it help reduce operational costs?",
      answer: "Absolutely — AI automation minimizes manpower load and prevents revenue loss."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-purple-50 to-blue-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 rounded-2xl">
                <Brain className="h-16 w-16 text-white" />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              AI Parking <span className="text-purple-600">Management Platform</span>
            </h1>
            <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-8">
              An Intelligent AI-Powered Parking Management Platform Built for Predictive Control, Automation & High-Performance Parking Operations
            </h2>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Parking has entered a new era — an era where decisions need to be faster than the traffic they manage. 
                Where efficiency can't depend on manual judgment or staff availability. Where revenue security and 
                operational accuracy are non-negotiable.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-red-900 mb-3">❌ Traditional Limitations</h3>
                  <p className="text-red-800">
                    Traditional systems can no longer keep up with the complexity and speed of modern parking environments.
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-blue-900 mb-3">🤖 AI-Driven Future</h3>
                  <p className="text-blue-800">
                    The future belongs to AI platforms that understand patterns, predict demand, and prevent bottlenecks.
                  </p>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-xl p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Park Hive delivers that future
                </h3>
                <p className="text-lg text-gray-700 mb-4">
                  A powerful AI Parking Management Platform designed to turn parking operations from 
                  reactive struggle into intelligent, predictive, self-optimizing performance.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <span className="text-red-600 line-through">Manual control</span>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <span className="text-red-600 line-through">Guess-based management</span>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <span className="text-purple-600 font-bold">Real intelligence</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-purple-700 transition-colors duration-200"
              >
                Request a Live Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="border border-purple-600 text-purple-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-purple-50 transition-colors duration-200"
              >
                Book a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What Is AI Platform */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              What Is an AI Parking Management Platform?
            </h2>
            
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 mb-12">
              <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                An AI Parking Management Platform uses artificial intelligence to analyze parking patterns, 
                predict peak loads, optimize slot allocation, automate operational workflows, and improve 
                the movement of vehicles through real-time decisioning and advanced analytics.
              </p>
              
              <div className="bg-white rounded-xl p-6 mb-6">
                <h3 className="text-2xl font-bold text-center text-gray-900 mb-4">
                  Instead of simply recording what already happened,<br />
                  <span className="text-purple-600">AI anticipates what will happen next</span>
                </h3>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
                AI learns from:
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {aiLearning.map((item, index) => (
                  <div key={index} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 text-center">
                    <Cpu className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                    <p className="font-medium text-gray-900">{item}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 text-center bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg p-6">
                <h4 className="text-xl font-bold">And it uses this intelligence to guide operations automatically.</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Now */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Why Parking Owners Need AI Now
            </h2>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Parking is no longer a manual logistics task — it's an efficiency business.
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <Clock className="h-8 w-8 text-red-600 mx-auto mb-2" />
                    <p className="text-red-900 font-medium">Every second of delay affects customer satisfaction</p>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <TrendingUp className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                    <p className="text-orange-900 font-medium">Every oversight affects revenue</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <Eye className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-purple-900 font-medium">Every blind spot creates risk</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-xl p-8">
                <h4 className="text-2xl font-bold text-center text-gray-900 mb-6">AI eliminates uncertainty</h4>
                <p className="text-lg text-center text-gray-700 mb-6">
                  It replaces human dependency with consistent, self-adapting automation.
                </p>
                
                <div className="space-y-3">
                  {aiEnsures.map((item, index) => (
                    <div key={index} className="flex items-center bg-white rounded-lg p-4">
                      <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <span className="text-white font-bold">✓</span>
                      </div>
                      <span className="text-gray-900 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="text-center bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl p-8">
              <h3 className="text-3xl font-bold">AI transforms parking into a system that thinks.</h3>
            </div>
          </div>
        </div>
      </section>

      {/* How AI Powers Operations */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              How AI Powers Smart Parking Operations
            </h2>
            
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 mb-12">
              <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                Park Hive uses AI technology to guide real-time parking decisions.<br />
                It analyzes live data and historical trends to:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {aiCapabilities.map((capability, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 flex items-center">
                    <Brain className="h-6 w-6 text-purple-600 mr-3 flex-shrink-0" />
                    <span className="text-gray-900 font-medium">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-red-900 mb-3">❌ Before AI</h4>
                <p className="text-red-800 text-lg">Reacting to chaos</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="text-xl font-bold text-green-900 mb-3">✅ With AI</h4>
                <p className="text-green-800 text-lg">Preventing chaos</p>
              </div>
            </div>
            
            <div className="mt-8 text-center bg-gradient-to-r from-purple-100 to-blue-100 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                The system becomes the brain of the parking ecosystem
              </h3>
              <p className="text-lg text-gray-700">
                Making operations faster, safer, and dramatically smarter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Intelligence Features */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Key Intelligence Features Parking Owners Depend On
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Park Hive's AI Parking Management Platform includes powerful capabilities such as:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {intelligenceFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                  <div className="flex items-start mb-3">
                    <div className="mr-4 mt-1">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {additionalFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                  <div className="flex items-center">
                    <Eye className="h-6 w-6 text-blue-600 mr-4 flex-shrink-0" />
                    <span className="font-medium text-gray-900">{feature}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl p-8">
              <h3 className="text-2xl font-bold">Operational clarity moves from minutes to milliseconds.</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Complex Environments */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Built for Complex, High-Density Environments
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <h4 className="text-xl font-bold text-red-900 mb-3">When pressure spikes</h4>
                <p className="text-red-800 text-lg">Manual systems collapse</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <h4 className="text-xl font-bold text-green-900 mb-3">AI advantage</h4>
                <p className="text-green-800 text-lg">AI thrives under load</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Whether it's:</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {scenarios.map((item, index) => (
                  <div key={index} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 text-center">
                    <div className="text-4xl mb-3">{item.icon}</div>
                    <p className="font-medium text-gray-900">{item.scenario}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 text-center bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  AI keeps flow organized and predictable, even when thousands move at once.
                </h4>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                <h4 className="text-xl font-bold text-blue-900 mb-2">This is not just efficiency.</h4>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <h4 className="text-xl font-bold text-green-900 mb-2">It's resilience.</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Impact */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              The Real Business Impact
            </h2>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
              <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                AI gives parking operators a competitive advantage that manual systems simply cannot match.
              </p>
              
              <div className="space-y-6">
                {businessImpacts.map((impact, index) => (
                  <div key={index} className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                      <div className="text-center">
                        <span className="text-red-600 line-through">{impact.before}</span>
                      </div>
                      <div className="text-center">
                        <span className="text-2xl">→</span>
                      </div>
                      <div className="text-center">
                        <span className="text-green-600 font-bold">{impact.after}</span>
                        <p className="text-sm text-gray-600 mt-1">{impact.benefit}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <h4 className="text-xl font-bold text-red-900 mb-2">Parking stops being guesswork.</h4>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <h4 className="text-xl font-bold text-green-900 mb-2">It becomes strategic, scientific performance.</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visionary Leaders */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
              Why Visionary Parking Leaders Choose Park Hive
            </h2>
            
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Because the industry is evolving — and leadership demands innovation.
              </h3>
              
              <p className="text-lg text-gray-700 mb-8">Park Hive empowers owners to:</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {visionaryBenefits.map((benefit, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 flex items-center">
                    <Target className="h-6 w-6 text-purple-600 mr-3 flex-shrink-0" />
                    <span className="text-gray-900 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl p-8 mb-8">
              <h3 className="text-2xl font-bold mb-4">
                Parking isn't a side service anymore — it's a strategic business asset.
              </h3>
              <p className="text-xl">AI turns that asset into advantage.</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-purple-700 transition-colors duration-200"
              >
                Request a Live Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Book a Demo
              </button>
            </div>
            
            <p className="text-gray-600 mt-6">
              Experience the intelligence behind smart parking.<br />
              See forecasting, analytics, and automated flow in action.<br />
              Understand how real-time AI transforms performance and profitability.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h4>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final Thought */}
      <section className="py-20 bg-gradient-to-br from-purple-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-12">Final Thought</h2>
            
            <div className="space-y-8 text-lg leading-relaxed">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-red-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-red-300 mb-2">❌ Efficiency is no longer enough</h4>
                </div>
                <div className="bg-green-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-green-300 mb-2">🧠 Predictive intelligence is the new standard</h4>
                </div>
              </div>
              
              <div className="space-y-6">
                <p className="text-xl">
                  AI is transforming industries everywhere — and parking will not be the exception.
                </p>
                <p className="text-xl font-bold text-yellow-300">
                  The smartest operations aren't lucky.
                </p>
                <p className="text-2xl font-bold">
                  They're engineered.
                </p>
                <p className="text-2xl font-bold text-purple-300">
                  Park Hive makes that intelligence real.
                </p>
              </div>
            </div>
            
            <div className="mt-12">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-white text-purple-900 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-colors duration-200 inline-flex items-center"
              >
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"/>
                </svg>
                Step into the next generation of parking automation
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIParkingManagementPlatform;